import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from '../../styles/admin/loginStyles';
import { loginUser } from '../../services/authService';
import { FaEye, FaEyeSlash } from 'react-icons/fa';


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('sessionExpired') === '1';

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const loginResponse = await loginUser(email, password, rememberMe);
      login(loginResponse);

      // Redirect based on role
      if (loginResponse.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      const errorMessage = err.message || 'Invalid credentials. Please try again.';

      // Check if error is due to unverified email
      if (errorMessage.includes('Email not verified')) {
        setError(
          <span>
            {errorMessage}{' '}
            <a
              href="/verification-pending"
              style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Resend verification email
            </a>
          </span>
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => navigate('/home');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.form}>
        <h2 style={styles.heading}>Login</h2>
        {!isAuthenticated ? (
          <>
            {sessionExpired && !error && (
              <p style={styles.errorMessage}>Your session has expired. Please log in again.</p>
            )}
            <div style={styles.inputGroup}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
                style={styles.inputField}
              />
            </div>

            <div style={styles.inputGroup}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                style={styles.inputField}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggleIcon}
                tabIndex="-1"
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              justifyContent: 'flex-start'
            }}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  marginRight: '8px',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px'
                }}
              />
              <label
                htmlFor="rememberMe"
                style={{
                  fontSize: '14px',
                  color: '#4a5568',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Remember me for 30 days
              </label>
            </div>

            {error && <p style={styles.errorMessage}>{error}</p>}

            <button
              style={{
                ...styles.submitBtn,
                backgroundColor: isLoading ? '#cccccc' : styles.submitBtn.backgroundColor,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <a
                href="/forgot-password"
                style={{ color: '#007bff', textDecoration: 'none', fontSize: '14px', marginRight: '15px' }}
              >
                Forgot Password?
              </a>
              <span style={{ color: '#6c757d' }}>|</span>
              <a
                href="/register"
                style={{ color: '#007bff', textDecoration: 'none', fontSize: '14px', marginLeft: '15px' }}
              >
                Register
              </a>
            </div>

            <button
              style={styles.homeBtn}
              onMouseOver={(e) => (e.target.style.backgroundColor = '#5a6268')}
              onMouseOut={(e) => (e.target.style.backgroundColor = '#6c757d')}
              onClick={handleGoHome}
            >
              Go Home
            </button>
          </>
        ) : (
          <button
            style={styles.logoutBtn}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#c82333')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#dc3545')}
            onClick={handleLogout}
          >
            Logout
          </button>
        )}
      </div>
    </div>
  );
};

export default Login;
